import React, { useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { ArrowLeft, CreditCard, Smartphone, Building2, Heart, Zap, Gift } from 'lucide-react';

const ChargeContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  font-size: 16px;
  color: #6b7280;
  cursor: pointer;
  margin-bottom: 30px;
  padding: 8px 0;
  
  &:hover {
    color: #374151;
  }
`;

const ChargeHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const ChargeTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 10px;
`;

const ChargeSubtitle = styled.p`
  color: #6b7280;
  font-size: 16px;
  line-height: 1.5;
`;

const CurrentPoints = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 30px;
  border-radius: 16px;
  text-align: center;
  margin-bottom: 40px;
  box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
`;

const PointsLabel = styled.div`
  font-size: 16px;
  opacity: 0.9;
  margin-bottom: 10px;
  font-weight: 500;
`;

const PointsValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ChargeOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 40px;
`;

const ChargeOption = styled.div`
  background: white;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  
  &:hover {
    border-color: #3b82f6;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.1);
  }
  
  ${props => props.selected && `
    background: #eff6ff;
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
  `}
`;

const ChargeAmount = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
`;

const ChargeBonus = styled.div`
  font-size: 14px;
  color: #ef4444;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 12px;
  text-transform: uppercase;
`;

const CustomAmount = styled.div`
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
`;

const CustomTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AmountInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
`;

const AmountButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
  flex-wrap: wrap;
`;

const AmountButton = styled.button`
  padding: 8px 16px;
  background: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  
  &:hover {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }
`;

const PaymentMethods = styled.div`
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
`;

const PaymentTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaymentOption = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fafb;
    border-color: #3b82f6;
  }
  
  &.selected {
    border-color: #3b82f6;
    background: #eff6ff;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const PaymentRadio = styled.input`
  margin-right: 12px;
  accent-color: #3b82f6;
`;

const PaymentLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
`;

const PaymentIcon = styled.div`
  margin-left: auto;
  font-size: 20px;
  color: #3b82f6;
`;

const ChargeSummary = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  border: 1px solid #e5e7eb;
`;

const SummaryTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 20px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 16px;
  
  &.total {
    font-size: 20px;
    font-weight: 700;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
    color: #3b82f6;
  }
`;

const ChargeButton = styled.button`
  width: 100%;
  padding: 18px;
  background: ${props => props.disabled ? '#9ca3af' : '#3b82f6'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
`;

const PointCharge = () => {
  const history = useHistory();
  const [selectedAmount, setSelectedAmount] = useState(0);
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');

  const chargeOptions = [
    { amount: 10000, bonus: 0 },
    { amount: 30000, bonus: 1000, popular: false },
    { amount: 50000, bonus: 2500, popular: true },
    { amount: 100000, bonus: 10000 },
    { amount: 200000, bonus: 25000 },
    { amount: 500000, bonus: 75000 }
  ];

  const paymentMethods = [
    { id: 'card', name: '신용카드', icon: <CreditCard size={20} /> },
    { id: 'account', name: '계좌이체', icon: <Building2 size={20} /> },
    { id: 'phone', name: '휴대폰 결제', icon: <Smartphone size={20} /> },
    { id: 'kakaopay', name: '카카오페이', icon: <Heart size={20} style={{color: '#fee500'}} /> },
    { id: 'naverpay', name: '네이버페이', icon: <Zap size={20} style={{color: '#03c75a'}} /> }
  ];

  const currentAmount = customAmount ? parseInt(customAmount) || 0 : selectedAmount;
  const bonusAmount = chargeOptions.find(opt => opt.amount === currentAmount)?.bonus || 0;
  const totalPoints = currentAmount + bonusAmount;

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (value) => {
    setCustomAmount(value);
    setSelectedAmount(0);
  };

  const handleQuickAmount = (amount) => {
    const current = parseInt(customAmount) || 0;
    setCustomAmount((current + amount).toString());
    setSelectedAmount(0);
  };

  const handleCharge = () => {
    if (currentAmount < 1000) {
      alert('최소 충전 금액은 1,000원입니다.');
      return;
    }

    const paymentMethodName = paymentMethods.find(pm => pm.id === paymentMethod)?.name;
    
    if (window.confirm(`${paymentMethodName}로 ${currentAmount.toLocaleString()}원을 충전하시겠습니까?\n보너스 ${bonusAmount.toLocaleString()}P가 추가로 적립됩니다.`)) {
      // 실제로는 결제 API 호출
      alert('결제가 완료되었습니다!\n포인트가 성공적으로 충전되었습니다.');
      history.push('/mypage');
    }
  };

  return (
    <ChargeContainer>
      <BackButton onClick={() => history.goBack()}>
        <ArrowLeft size={16} />
        마이페이지로 돌아가기
      </BackButton>

      <ChargeHeader>
        <ChargeTitle>포인트 충전</ChargeTitle>
        <ChargeSubtitle>다양한 결제 수단으로 편리하게 포인트를 충전하세요<br/>높은 금액 충전 시 보너스 포인트를 드립니다!</ChargeSubtitle>
      </ChargeHeader>

      <CurrentPoints>
        <PointsLabel>현재 보유 포인트</PointsLabel>
        <PointsValue>
          <Gift size={24} />
          30,000 P
        </PointsValue>
      </CurrentPoints>

      <ChargeOptions>
        {chargeOptions.map((option) => (
          <ChargeOption
            key={option.amount}
            selected={selectedAmount === option.amount}
            onClick={() => handleAmountSelect(option.amount)}
          >
            {option.popular && <PopularBadge>인기</PopularBadge>}
            <ChargeAmount>₩{option.amount.toLocaleString()}</ChargeAmount>
            {option.bonus > 0 && (
              <ChargeBonus>
                <Gift size={16} />
                +{option.bonus.toLocaleString()}P 보너스
              </ChargeBonus>
            )}
          </ChargeOption>
        ))}
      </ChargeOptions>

      <CustomAmount>
        <CustomTitle>
          <Zap size={20} />
          직접 입력
        </CustomTitle>
        <AmountInput
          type="number"
          placeholder="충전할 금액을 입력하세요 (최소 1,000원)"
          value={customAmount}
          onChange={(e) => handleCustomAmountChange(e.target.value)}
        />
        <AmountButtons>
          {[1000, 5000, 10000, 50000, 100000].map((amount) => (
            <AmountButton
              key={amount}
              onClick={() => handleQuickAmount(amount)}
            >
              +₩{amount.toLocaleString()}
            </AmountButton>
          ))}
        </AmountButtons>
      </CustomAmount>

      <PaymentMethods>
        <PaymentTitle>
          <CreditCard size={20} />
          결제 수단 선택
        </PaymentTitle>
        {paymentMethods.map((method) => (
          <PaymentOption
            key={method.id}
            className={paymentMethod === method.id ? 'selected' : ''}
            onClick={() => setPaymentMethod(method.id)}
          >
            <PaymentRadio
              type="radio"
              checked={paymentMethod === method.id}
              onChange={() => setPaymentMethod(method.id)}
            />
            <PaymentLabel>
              {method.name}
            </PaymentLabel>
            <PaymentIcon>{method.icon}</PaymentIcon>
          </PaymentOption>
        ))}
      </PaymentMethods>

      {currentAmount > 0 && (
        <ChargeSummary>
          <SummaryTitle>충전 내역</SummaryTitle>
          <SummaryRow>
            <span>충전 금액</span>
            <span>₩{currentAmount.toLocaleString()}</span>
          </SummaryRow>
          {bonusAmount > 0 && (
            <SummaryRow>
              <span>보너스 포인트</span>
              <span style={{ color: '#ef4444' }}>+{bonusAmount.toLocaleString()}P</span>
            </SummaryRow>
          )}
          <SummaryRow className="total">
            <span>총 적립 포인트</span>
            <span>{totalPoints.toLocaleString()}P</span>
          </SummaryRow>
        </ChargeSummary>
      )}

      <ChargeButton
        disabled={currentAmount < 1000}
        onClick={handleCharge}
      >
        {currentAmount >= 1000 ? `₩${currentAmount.toLocaleString()} 충전하기` : '최소 1,000원 이상 입력해주세요'}
      </ChargeButton>
    </ChargeContainer>
  );
};

export default PointCharge;